import { useState } from "react";
import toast from "react-hot-toast";

import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { useVerifyPassword } from "@/hooks/mutations/useAuthMutations";
import * as authService from "@/services/authService";

/**
 * Email + password sign-in.
 *
 * Rendered only when the resolved tenant has the method enabled. The server enforces that
 * independently, so this is about not showing a form that cannot work — not about access control.
 */
export function LoginPasswordForm() {
  const verifyPassword = useVerifyPassword();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [forgotSent, setForgotSent] = useState(false);
  const [sendingForgot, setSendingForgot] = useState(false);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setLocalError(null);

    if (!email.trim() || !password) {
      setLocalError("Enter your email address and password.");
      return;
    }

    verifyPassword.mutate({ email: email.trim(), password });
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      setLocalError("Enter your email address first, then choose Forgot password.");
      return;
    }

    setLocalError(null);
    setSendingForgot(true);

    try {
      await authService.forgotPassword(email.trim());
      // Deliberately unconditional: the server does not reveal whether the address has an account,
      // so neither can this message.
      setForgotSent(true);
      toast.success("If that email address has an account, a reset link has been sent.");
    } catch {
      toast.error("Could not start a password reset. Please try again shortly.");
    } finally {
      setSendingForgot(false);
    }
  }

  const busy = verifyPassword.isPending || sendingForgot;

  return (
    <form onSubmit={handleSubmit} className="login-fields">
      <div>
        <label htmlFor="login-email" className="login-label">
          Email address
        </label>
        <Input
          id="login-email"
          type="email"
          autoComplete="username"
          aria-describedby={localError ? "login-password-error" : undefined}
          value={email}
          disabled={busy}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@school.edu"
        />
      </div>

      <div>
        <label htmlFor="login-password" className="login-label">
          Password
        </label>
        <Input
          id="login-password"
          type="password"
          autoComplete="current-password"
          aria-describedby={localError ? "login-password-error" : undefined}
          value={password}
          disabled={busy}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Your password"
        />
      </div>

      {localError && (
        <p id="login-password-error" className="login-error" role="alert">
          {localError}
        </p>
      )}

      {forgotSent && (
        <p className="login-helper" role="status">
          Check your inbox for a link to set a new password. It expires in 30 minutes.
        </p>
      )}

      <Button type="submit" className="w-full" disabled={busy}>
        {verifyPassword.isPending ? "Signing in…" : "Sign in"}
      </Button>

      <button
        type="button"
        onClick={() => void handleForgotPassword()}
        disabled={busy}
        className="login-text-button w-full"
      >
        {sendingForgot ? "Sending…" : "Forgot password?"}
      </button>
    </form>
  );
}
